import { Anchor } from 'src/design/atoms/Anchor';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { Table } from 'src/design/molecules/Table';
import React from 'react';

import {
  IntegrationStatusDto,
  JobCostCodeSummaryGpDto,
  JobCostDetailDto,
  JobPoDataDto,
} from 'src/api/FiretrolApiClient';

import { Currency } from 'src/design/atoms/Currency';
import {
  CATEGORY_FOOTER_BG,
  HEADER_ADJ_BG,
  HEADER_GROUP_BG,
  TOTAL_BG,
  YELLOW,
} from 'src/routes/CTC/ViewCTCJob/components/colors';

import {
  AggregateRow,
  AggregateTotals,
  CategoryGroup,
  useCostCodeAggregates,
} from './useCostCodeAggregates';

const HEADER_OU_BG = '#f6f6f6';
const COL_HEADER_BG = '#e9e9e9';
const HEADER_BORDER = '1px solid #e0e0e0';

interface Column {
  id: string;
  label: string;
}

const columns: Column[] = [
  { id: 'description', label: 'Description' },
  { id: 'original', label: 'Original' },
  { id: 'revised', label: 'Revised' },
  { id: 'committed', label: 'Committed' },
  { id: 'jtd', label: 'JTD' },
  { id: 'month_to_date', label: 'MTD' },
  { id: 'est', label: 'EST' },
  { id: 'jtd_month', label: 'JTD' },
  { id: 'mtd', label: 'MTD' },
  { id: 'remain', label: 'Remain' },
  { id: 'rate', label: 'Rate' },
  { id: 'ctc_hours', label: 'CTC_Hours' },
  { id: 'jtd_rate', label: 'JTD_Rate' },
  { id: 'CTC', label: 'CTC' },
  { id: 'cost_at_completion', label: 'C@C' },
  { id: 'currentDiff', label: 'Over/Under' },
];

const categoryFooterCellStyle: React.CSSProperties = {
  borderBottom: '1px solid #000',
  backgroundColor: CATEGORY_FOOTER_BG,
};

const grandFooterCellStyle: React.CSSProperties = {
  borderBottom: '1px solid #000',
  backgroundColor: TOTAL_BG,
};

const renderCostOrParens = (value: number): React.ReactNode =>
  value < 0 ? (
    <span style={{ color: 'red' }}>
      (<Currency precise value={Math.abs(value)} />)
    </span>
  ) : (
    <Currency precise value={value} />
  );

const renderDiff = (value: number): React.ReactNode =>
  value < 0 ? (
    <span style={{ color: 'red' }}>
      (<Currency precise value={Math.abs(value)} />)
    </span>
  ) : (
    <span style={{ color: 'green' }}>
      <Currency precise value={value} />
    </span>
  );

export interface LineSelection {
  costCode: string;
  description: string;
  isLabor: boolean;
  isMtd?: boolean;
}

export interface MaterialsSelection {
  costCode: string;
  description: string;
  poNumber: string;
}

interface CostCodeRowProps {
  row: AggregateRow;
  onSelectLine: (selection: LineSelection) => void;
  onSelectMaterials: (selection: MaterialsSelection) => void;
}

const CostCodeRow: React.FC<CostCodeRowProps> = ({
  row,
  onSelectLine,
  onSelectMaterials,
}) => {
  const { costCode, description, isLabor } = row;
  return (
    <Table.Tbody key={costCode}>
      <Table.Tr tabIndex={-1}>
        <Table.Td align="left" colSpan={1}>
          <Group gap="sm" wrap="nowrap">
            <Text
              component="span"
              ta="right"
              style={{
                width: 70,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {costCode}
            </Text>
            <Text c="black" component="span">
              {description}
            </Text>
          </Group>
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {<Currency precise value={row.originalEstimateCost} />}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {<Currency precise value={row.revisedEstimateCost} />}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {!isLabor ? (
            <Anchor
              onClick={() =>
                onSelectMaterials({
                  costCode,
                  description: '',
                  poNumber: '123',
                })
              }
            >
              {<Currency precise value={row.remainingPoBalance} />}
            </Anchor>
          ) : (
            <>-</>
          )}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          <Anchor
            onClick={() => onSelectLine({ costCode, description, isLabor })}
          >
            {<Currency precise value={row.jtdCost} />}
          </Anchor>
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          <Anchor
            onClick={() =>
              onSelectLine({ costCode, description, isLabor, isMtd: true })
            }
          >
            {<Currency precise value={row.mtdAmount} />}
          </Anchor>
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {isLabor ? Math.round(row.estHours).toLocaleString() : '-'}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {isLabor ? (
            <Anchor
              onClick={() => onSelectLine({ costCode, description, isLabor })}
            >
              {Math.round(row.jtdHours).toLocaleString()}
            </Anchor>
          ) : (
            '-'
          )}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {isLabor ? (
            <Anchor
              onClick={() =>
                onSelectLine({ costCode, description, isLabor, isMtd: true })
              }
            >
              {Math.round(row.mtdHours).toLocaleString()}
            </Anchor>
          ) : (
            '-'
          )}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {isLabor
            ? Math.round(row.estHours - row.jtdHours).toLocaleString()
            : '-'}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {isLabor ? <Currency precise value={row.estimatedRate} /> : <>-</>}
        </Table.Td>
        <Table.Td
          align="right"
          colSpan={1}
          style={{ backgroundColor: isLabor ? YELLOW : 'inherit' }}
        >
          {isLabor ? Number(row.ctcHours).toLocaleString() : <>-</>}
        </Table.Td>
        <Table.Td
          align="right"
          colSpan={1}
          style={{ backgroundColor: isLabor ? YELLOW : 'inherit' }}
        >
          {isLabor ? <Currency precise value={row.jtdRate} /> : <>-</>}
        </Table.Td>
        <Table.Td align="right" colSpan={1} style={{ backgroundColor: YELLOW }}>
          {<Currency precise value={row.ctc} />}
        </Table.Td>
        <Table.Td align="right" colSpan={1} style={{ backgroundColor: YELLOW }}>
          {renderCostOrParens(row.cost)}
        </Table.Td>
        <Table.Td align="right" colSpan={1}>
          {renderDiff(row.diff)}
        </Table.Td>
      </Table.Tr>
    </Table.Tbody>
  );
};

interface CategoryTotalsRowProps {
  category: string;
  isLabor: boolean;
  totals: AggregateTotals;
}

const CategoryTotalsRow: React.FC<CategoryTotalsRowProps> = ({
  category,
  isLabor,
  totals,
}) => (
  <Table.Tbody className="ctc-table-footer">
    <Table.Tr>
      <Table.Td align="left" style={categoryFooterCellStyle} colSpan={1}>
        <b>Total {category}:</b>
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.originalEstimateCost} />}</b>
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.revisedEstimateCost} />}</b>
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        {!isLabor ? (
          <b>{<Currency precise value={totals.remainingPoBalance} />}</b>
        ) : (
          <>-</>
        )}
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.jtdCost} />}</b>
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.mtdAmount} />}</b>
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        {isLabor ? <b>{Math.round(totals.estHours).toLocaleString()}</b> : '-'}
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        {isLabor ? <b>{Math.round(totals.jtdHours).toLocaleString()}</b> : '-'}
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        {isLabor ? <b>{Math.round(totals.mtdHours).toLocaleString()}</b> : '-'}
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        {isLabor ? <b>{totals.remain.toLocaleString()}</b> : '-'}
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        -
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        -
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        -
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.ctc} />}</b>
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        <b>{renderCostOrParens(totals.cost)}</b>
      </Table.Td>
      <Table.Td style={categoryFooterCellStyle} align="right" colSpan={1}>
        <b>{renderDiff(totals.diff)}</b>
      </Table.Td>
    </Table.Tr>
  </Table.Tbody>
);

interface GrandTotalsRowProps {
  totals: AggregateTotals;
}

const GrandTotalsRow: React.FC<GrandTotalsRowProps> = ({ totals }) => (
  <Table.Tbody className="ctc-table-footer">
    <Table.Tr>
      <Table.Td align="left" style={grandFooterCellStyle} colSpan={1}>
        <b>Total Job:</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.originalEstimateCost} />}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.revisedEstimateCost} />}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.remainingPoBalance} />}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.jtdCost} />}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.mtdAmount} />}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{Math.round(totals.estHours).toLocaleString()}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{Math.round(totals.jtdHours).toLocaleString()}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{Math.round(totals.mtdHours).toLocaleString()}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{totals.remain.toLocaleString()}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        -
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        -
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        -
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{<Currency precise value={totals.ctc} />}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{renderCostOrParens(totals.cost)}</b>
      </Table.Td>
      <Table.Td style={grandFooterCellStyle} align="right" colSpan={1}>
        <b>{renderDiff(totals.diff)}</b>
      </Table.Td>
    </Table.Tr>
  </Table.Tbody>
);

interface CostCodeCategoryGroupProps {
  group: CategoryGroup;
  onSelectLine: (selection: LineSelection) => void;
  onSelectMaterials: (selection: MaterialsSelection) => void;
}

const CostCodeCategoryGroup: React.FC<CostCodeCategoryGroupProps> = ({
  group,
  onSelectLine,
  onSelectMaterials,
}) => (
  <>
    {group.rows.map((row) => (
      <CostCodeRow
        key={row.costCode}
        row={row}
        onSelectLine={onSelectLine}
        onSelectMaterials={onSelectMaterials}
      />
    ))}
    {group.rows.length > 0 && (
      <CategoryTotalsRow
        category={group.category}
        isLabor={group.isLabor}
        totals={group.totals}
      />
    )}
  </>
);

const TableHeader: React.FC = () => (
  <Table.Thead data-testid="ctc-cost-code-table-head">
    <Table.Tr>
      <Table.Td
        align="center"
        colSpan={1}
        style={{ backgroundColor: HEADER_GROUP_BG, borderLeft: HEADER_BORDER }}
      >
        Cost Codes
      </Table.Td>
      <Table.Td
        align="center"
        colSpan={2}
        style={{ backgroundColor: HEADER_GROUP_BG, borderLeft: HEADER_BORDER }}
      >
        Estimates - System
      </Table.Td>
      <Table.Td
        align="center"
        colSpan={3}
        style={{ backgroundColor: HEADER_GROUP_BG, borderLeft: HEADER_BORDER }}
      >
        Actuals
      </Table.Td>
      <Table.Td
        align="center"
        colSpan={3}
        style={{ backgroundColor: HEADER_GROUP_BG, borderLeft: HEADER_BORDER }}
      >
        Hours
      </Table.Td>
      <Table.Td
        align="center"
        colSpan={2}
        style={{ backgroundColor: HEADER_GROUP_BG, borderLeft: HEADER_BORDER }}
      >
        Current Est
      </Table.Td>
      <Table.Td
        align="center"
        colSpan={4}
        style={{ backgroundColor: HEADER_ADJ_BG, borderLeft: HEADER_BORDER }}
      >
        Current Month Adjustments
      </Table.Td>
      <Table.Td
        align="center"
        colSpan={4}
        style={{ backgroundColor: HEADER_OU_BG, borderLeft: HEADER_BORDER }}
      >
        O/U
      </Table.Td>
    </Table.Tr>
    <Table.Tr>
      {columns.map((column) => (
        <Table.Td
          key={column.id}
          align="center"
          style={{ backgroundColor: COL_HEADER_BG }}
          colSpan={1}
        >
          {column.label}
        </Table.Td>
      ))}
    </Table.Tr>
  </Table.Thead>
);

interface CostCodeTableProps {
  summaryList: JobCostCodeSummaryGpDto[];
  details: Record<string, JobCostDetailDto[]>;
  materials: Record<string, JobPoDataDto[]>;
  status: IntegrationStatusDto | null;
  onSelectLine: (selection: LineSelection) => void;
  onSelectMaterials: (selection: MaterialsSelection) => void;
  /** CSS length held by content pinned between the app header and the table. */
  pinnedAbove?: string;
}

const MIN_SCROLL_HEIGHT = 320;
const PAPER_PADDING = 'md';

// The trailing 2px is the wrapping Paper's top and bottom border.
const scrollMaxHeight = (pinnedAbove = '0px'): string =>
  `max(${MIN_SCROLL_HEIGHT}px, calc(100dvh - var(--app-shell-header-height, 0px) - ${pinnedAbove} - 2 * var(--mantine-spacing-${PAPER_PADDING}) - 2px))`;

export const CostCodeTable: React.FC<CostCodeTableProps> = ({
  summaryList,
  details,
  materials,
  status,
  onSelectLine,
  onSelectMaterials,
  pinnedAbove,
}) => {
  const { groups, grandTotals } = useCostCodeAggregates({
    summaryList,
    details,
    materials,
    status,
  });

  return (
    <Paper p={PAPER_PADDING}>
      <Table.ScrollContainer
        type="native"
        minWidth={800}
        style={{ maxHeight: scrollMaxHeight(pinnedAbove) }}
        data-testid="ctc-cost-code-table-scroll"
      >
        <Table stickyHeader>
          <TableHeader />
          {groups.map((group) => (
            <CostCodeCategoryGroup
              key={group.category}
              group={group}
              onSelectLine={onSelectLine}
              onSelectMaterials={onSelectMaterials}
            />
          ))}
          <GrandTotalsRow totals={grandTotals} />
        </Table>
      </Table.ScrollContainer>
    </Paper>
  );
};
